import React from 'react';
import Svg, {Line} from 'react-native-svg';
import {BALLS} from '../../constant';
import {cn} from '@/utils';
const NUMBER = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export interface AnalyzeItemProps {
  index?: number;
  issueNum?: string;
  digit?: string;
  nextDigit?: string;
  color?: string;
}

const AnalyzeItem = ({
  index = 0,
  digit = '0',
  issueNum,
  nextDigit,
  color,
}: AnalyzeItemProps) => {
  const selectedBallBg = React.useMemo(() => {
    const source =
      color === 'g'
        ? BALLS.colorGreen
        : color === 'r'
        ? BALLS.colorRed
        : color === 'g,v'
        ? BALLS.colorGreenV
        : color === 'r,v'
        ? BALLS.colorRedV
        : undefined;
    if (!source) {
      return undefined;
    }
    return typeof source === 'string'
      ? source
      : source?.default || source?.src || undefined;
  }, [color]);

  const renderBall = (current: string) => {
    if (current === digit) {
      return (
        <div
          className="font-bold flex items-center justify-center text-center text-main text-white w-5 h-5 bg-center bg-no-repeat rounded-full overflow-hidden"
          style={
            selectedBallBg
              ? ({
                  backgroundImage: `url(${selectedBallBg})`,
                  backgroundSize: '100% 100%',
                } as React.CSSProperties)
              : undefined
          }>
          <span className="text-[0.46875rem]">{digit}</span>
        </div>
      );
    }
    return (
      <div className="font-bold flex items-center justify-center text-center text-main w-5 h-5 rounded-full border border-[#9FA5AC]">
        <span className="text-[0.46875rem] text-[#3E4558]">{current}</span>
      </div>
    );
  };

  const connector = React.useMemo(() => {
    if (nextDigit === undefined || digit === undefined) {
      return null;
    }
    const d1 = Number(digit);
    const d2 = Number(nextDigit);
    if (Number.isNaN(d1) || Number.isNaN(d2)) {
      return null;
    }
    const width = d2 === d1 ? 0 : Math.abs(d2 - d1) * (20 + 2) - 2;
    const height = 48;
    const widthRem = (width || 2) / 16;
    const heightRem = height / 16;
    const leftRem = (Math.min(d1, d2) * (20 + 2) - 2 + 10) / 16;

    return (
      <div
        className="absolute top-[50%] z-[1] pointer-events-none"
        style={{left: `${leftRem}rem`}}>
        <Svg
          fill="none"
          width={`${widthRem}rem`}
          height={`${heightRem}rem`}
          viewBox={`0 0 ${width || 2} ${height}`}
          stroke="#E20000">
          {d2 >= d1 ? (
            <Line
              x1={width === 0 ? 1 : 0}
              y1={0}
              x2={width === 0 ? 1 : width}
              y2={height}
              strokeWidth={2}
            />
          ) : (
            <Line x1={0} y1={height} x2={width} y2={0} strokeWidth={2} />
          )}
        </Svg>
      </div>
    );
  }, [digit, nextDigit]);

  return (
    <div
      className={cn(
        'flex flex-row text-sm text-sec py-0',
        index % 2 === 0 ? 'bg-[#F8F9FF]' : 'bg-white',
      )}>
      <div className="flex flex-row flex-1 items-center mx-3 h-12">
        <div className="flex-1 text-left text-xs">{issueNum}</div>
        <div className="flex flex-row justify-end gap-x-0.5 relative">
          {connector}
          {NUMBER.map((item, _i) => (
            <div key={_i} className="z-[7]">
              {renderBall(item)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyzeItem;
